<template>
  <div class="app-container schedule-page-admin">
    <div class="sp-admin-toolbar">
      <div class="sp-admin-title">
        <h2>上映劇場ページ</h2>
        <p>作品の上映劇場・上映時間を掲載する公開ページを作成し、リンクを公式サイトなどに掲載できます。</p>
      </div>
      <div class="sp-admin-actions">
        <el-button icon="Refresh" @click="loadList">再読み込み</el-button>
        <el-button type="primary" icon="Plus" @click="openCreate">新規作成</el-button>
      </div>
    </div>

    <el-table v-loading="listLoading" :data="list" border>
      <el-table-column label="作品" min-width="220">
        <template #default="{ row }">
          <div class="sp-admin-movie">{{ row.movieTitle || '—' }}</div>
        </template>
      </el-table-column>
      <el-table-column label="配色" width="150">
        <template #default="{ row }">
          <span class="sp-admin-swatch-inline" :class="`sp-theme-${normalizeStyle(row.style)}`">
            <i class="sp-swatch-bg"><i class="sp-swatch-accent" /></i>
          </span>
          {{ styleLabel(row.style) }}
        </template>
      </el-table-column>
      <el-table-column label="表示形式" width="190">
        <template #default="{ row }">{{ layoutLabel(row.layout) }}</template>
      </el-table-column>
      <el-table-column label="劇場数" width="80" align="right" prop="cinemaCount" />
      <el-table-column label="公開" width="90" align="center">
        <template #default="{ row }">
          <el-switch
            :model-value="!!row.enabled"
            :loading="row._switching"
            @change="(val) => toggleEnabled(row, val)"
          />
        </template>
      </el-table-column>
      <el-table-column label="更新日時" width="170" prop="updateTime" />
      <el-table-column label="操作" width="360" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" icon="Edit" @click="openEdit(row)">編集</el-button>
          <el-button link type="primary" icon="CopyDocument" @click="copyLink(row.pageKey)">リンクをコピー</el-button>
          <el-button link type="primary" icon="View" @click="openPreview(row.pageKey)">表示</el-button>
          <el-button link type="danger" icon="Delete" @click="handleDelete(row)">削除</el-button>
        </template>
      </el-table-column>
      <template #empty>
        <div class="sp-admin-empty">まだページがありません。「新規作成」から作成してください。</div>
      </template>
    </el-table>

    <!-- 新建 / 编辑 -->
    <el-dialog
      v-model="dialogVisible"
      :title="form.pageKey ? 'ページを編集' : 'ページを作成'"
      width="960px"
      :fullscreen="isMobile"
      :close-on-click-modal="false"
      append-to-body
      destroy-on-close
    >
      <div v-loading="detailLoading">
        <el-form ref="formRef" :model="form" :rules="rules" label-width="110px" label-position="top">
          <!-- 作品：新建时可选，编辑时不可修改（后端会忽略） -->
          <el-form-item label="作品" prop="movieId">
            <!-- 作品候选按公开日新→旧排列（sortOrder=desc）：首屏是最新的作品，可关键字搜索，滚动加载更多 -->
            <LazyLoadMovieSelect
              v-if="!form.pageKey"
              v-model:model-value="form.movieId"
              :list="movieOptions"
              :fetch-options="fetchMovieOptions"
              :company-id="companyId"
              :is-fetch="movieOptions.length >= MOVIE_PAGE_SIZE"
              show-open-date
              placeholder="作品名を入力して検索"
              :style="{ width: '100%' }"
            />
            <div v-else class="sp-admin-movie-fixed">
              {{ form.movieTitle }}
              <span class="sp-admin-help">作品は作成後に変更できません</span>
            </div>
          </el-form-item>

          <el-form-item label="配色">
            <div class="sp-style-grid">
              <button
                v-for="opt in STYLE_OPTIONS"
                :key="opt.value"
                type="button"
                class="sp-style-card"
                :class="[`sp-theme-${opt.value}`, { 'is-selected': form.style === opt.value }]"
                :aria-pressed="form.style === opt.value"
                @click="form.style = opt.value"
              >
                <span class="sp-style-preview">
                  <span class="sp-style-title">{{ opt.label }}</span>
                  <span class="sp-style-times">
                    <span>10:00</span><span>13:30</span>
                  </span>
                </span>
                <span class="sp-style-desc">{{ opt.desc }}</span>
              </button>
            </div>
          </el-form-item>

          <el-form-item label="表示形式">
            <el-radio-group v-model="form.layout">
              <el-radio v-for="opt in LAYOUT_OPTIONS" :key="opt.value" :label="opt.value">{{ opt.label }}</el-radio>
            </el-radio-group>
          </el-form-item>

          <el-form-item label="ページ備考" prop="pageNote">
            <el-input
              v-model="form.pageNote"
              type="textarea"
              :rows="3"
              maxlength="2000"
              show-word-limit
              placeholder="ページ上部に表示されます（例：舞台挨拶の実施劇場は…）"
            />
          </el-form-item>

          <!-- 追加項目：劇場の表に列を追加する（最多 3 列，顺序即显示顺序） -->
          <el-form-item label="追加項目">
            <div class="sp-extra-columns">
              <p class="sp-admin-help">
                劇場の表に列を追加できます（最大 {{ MAX_EXTRA_COLUMNS }} 項目）。各劇場の値は下の劇場一覧で入力してください。
              </p>
              <div v-for="(col, i) in form.extraColumns" :key="col.key" class="sp-extra-column-row">
                <el-input
                  v-model="col.title"
                  maxlength="50"
                  show-word-limit
                  placeholder="項目名（例：日本語字幕付き上映）"
                />
                <el-button link icon="Top" :disabled="i === 0" @click="moveExtraColumn(i, -1)" />
                <el-button link icon="Bottom" :disabled="i === form.extraColumns.length - 1" @click="moveExtraColumn(i, 1)" />
                <el-button link type="danger" icon="Delete" @click="removeExtraColumn(i)" />
              </div>
              <div>
                <el-button
                  icon="Plus"
                  :disabled="form.extraColumns.length >= MAX_EXTRA_COLUMNS"
                  @click="addExtraColumn"
                >
                  追加項目を追加
                </el-button>
              </div>
            </div>
          </el-form-item>

          <el-form-item label="上映劇場">
            <div class="sp-cinema-picker">
              <p class="sp-admin-help">
                上映スケジュールが公開される前でも劇場を登録できます。上映スケジュールのある劇場はタイムテーブルで、まだない劇場は地区別の表で表示されます。スケジュールが公開されると、ここに登録していない劇場も自動で表示されます。
              </p>
              <div class="sp-cinema-picker-row">
                <el-select
                  v-model="pickerPref"
                  clearable
                  filterable
                  placeholder="都道府県"
                  class="sp-cinema-picker-pref"
                >
                  <el-option v-for="p in prefOptions" :key="p" :label="p" :value="p" />
                </el-select>
                <el-select-v2
                  v-model="pickerCinemaId"
                  :options="cinemaOptions"
                  filterable
                  clearable
                  :loading="cinemaLoading"
                  placeholder="劇場名で検索して追加"
                  class="sp-cinema-picker-cinema"
                  @change="addCinema"
                />
              </div>
              <p v-if="cinemaLoadError" class="sp-admin-error">
                劇場一覧を取得できませんでした。
                <el-button link type="primary" @click="loadCinemaMaster">再試行</el-button>
              </p>

              <el-table :data="form.cinemas" border size="small" class="sp-cinema-table" max-height="420">
                <el-table-column label="#" width="48" align="center">
                  <template #default="{ $index }">{{ $index + 1 }}</template>
                </el-table-column>
                <el-table-column label="劇場" min-width="200">
                  <template #default="{ row }">
                    <div>{{ row.cinemaName }}</div>
                    <div class="sp-admin-help">{{ prefOf(row.eglCinemaId) }}</div>
                  </template>
                </el-table-column>
                <!-- 電話番号：添加时预填默认值，可修改；空欄 = 公開ページに表示しない -->
                <el-table-column key="tel" label="電話番号" width="160">
                  <template #default="{ row }">
                    <el-input
                      v-model="row.telephone"
                      maxlength="30"
                      clearable
                      :placeholder="telephonePlaceholder(row)"
                    />
                  </template>
                </el-table-column>
                <!-- 追加項目：按顺序一列一个，列名即项目名 -->
                <el-table-column
                  v-for="(col, i) in form.extraColumns"
                  :key="col.key"
                  :label="col.title.trim() || `追加項目${i + 1}`"
                  min-width="180"
                >
                  <template #default="{ row }">
                    <el-input
                      v-model="row.extraValues[i]"
                      type="textarea"
                      :autosize="{ minRows: 1, maxRows: 4 }"
                      maxlength="200"
                      placeholder="任意（例：11/14(土)〜11/17(火)）"
                    />
                  </template>
                </el-table-column>
                <el-table-column key="note" label="備考" min-width="240">
                  <template #default="{ row }">
                    <el-input
                      v-model="row.note"
                      type="textarea"
                      :autosize="{ minRows: 1, maxRows: 4 }"
                      maxlength="500"
                      placeholder="任意（例：IMAX上映あり）"
                    />
                  </template>
                </el-table-column>
                <el-table-column label="" width="130" align="center">
                  <template #default="{ $index }">
                    <el-button link icon="Top" :disabled="$index === 0" @click="moveCinema($index, -1)" />
                    <el-button link icon="Bottom" :disabled="$index === form.cinemas.length - 1" @click="moveCinema($index, 1)" />
                    <el-button link type="danger" icon="Delete" @click="form.cinemas.splice($index, 1)" />
                  </template>
                </el-table-column>
                <template #empty>
                  <span class="sp-admin-help">登録された劇場はありません</span>
                </template>
              </el-table>
              <div class="sp-admin-help">
                {{ form.cinemas.length }} / {{ MAX_CINEMAS }} 劇場・上から順に表示されます。電話番号を空欄にすると、グレーで表示されている既定の番号が公開ページに表示されます。
              </div>
            </div>
          </el-form-item>

          <el-form-item v-if="form.pageKey" label="公開リンク">
            <div class="sp-link-row">
              <el-input :model-value="buildPublicUrl(form.pageKey)" readonly />
              <el-button icon="CopyDocument" @click="copyLink(form.pageKey)">コピー</el-button>
            </div>
          </el-form-item>
        </el-form>
      </div>
      <template #footer>
        <el-button @click="dialogVisible = false">キャンセル</el-button>
        <el-button type="primary" :loading="saving" @click="submit">保存</el-button>
      </template>
    </el-dialog>

    <!-- 保存成功后展示公开链接 -->
    <el-dialog v-model="linkDialogVisible" title="保存しました" width="560px" append-to-body>
      <p>公開リンクをコピーして、公式サイトなどに掲載してください。</p>
      <div class="sp-link-row">
        <el-input :model-value="buildPublicUrl(savedPageKey)" readonly />
        <el-button type="primary" icon="CopyDocument" @click="copyLink(savedPageKey)">コピー</el-button>
      </div>
      <template #footer>
        <el-button @click="openPreview(savedPageKey)">ページを表示</el-button>
        <el-button type="primary" @click="linkDialogVisible = false">閉じる</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
/**
 * 作品上映排期页・CMS 配置（DEV-15088）
 * 选作品、选上映影院（排期确定前也能选）、填页面备注和每家影院备注、选配色和显示方式，生成并复制公开链接。
 * 作品用 DP 的 movieId（后端解析成 EP 的 eglMovieId），影院用 EP 的 eglCinemaId。
 */
import { computed, onMounted, reactive, ref } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { useWindowSize } from '@vueuse/core';
import { getMovieListService } from '@/api/dp/index.js';
import LazyLoadMovieSelect from '@/components/LazyLoadMovieSelect/index.vue';
import {
  createSchedulePageService,
  getCinemaContactsService,
  deleteSchedulePageService,
  getSchedulePageService,
  listSchedulePageService,
  setSchedulePageEnabledService,
  updateSchedulePageService,
} from '@/api/dp/schedulePage';
import { fetchEpCinemaList } from '@/api/dp/epWeb';
import {
  buildPublicUrl, DEFAULT_LAYOUT, DEFAULT_STYLE, LAYOUT_OPTIONS, normalizeLayout, normalizeStyle, STYLE_OPTIONS,
} from './constants';

const MAX_CINEMAS = 1000;
// 追加項目（表的附加列）最多 3 列
const MAX_EXTRA_COLUMNS = 3;
const companyId = window.localStorage.getItem('deptId');

const { width } = useWindowSize();
const isMobile = computed(() => width.value < 768);

const styleLabel = (v) => STYLE_OPTIONS.find((o) => o.value === normalizeStyle(v))?.label;
const layoutLabel = (v) => LAYOUT_OPTIONS.find((o) => o.value === normalizeLayout(v))?.label;

// ---------- 列表 ----------
const list = ref([]);
const listLoading = ref(false);

async function loadList() {
  listLoading.value = true;
  try {
    const res = await listSchedulePageService();
    list.value = (Array.isArray(res?.data) ? res.data : []).map((row) => ({ ...row, _switching: false }));
  } catch (e) {
    console.error(e);
  } finally {
    listLoading.value = false;
  }
}

// 公开 / 停止公开
async function toggleEnabled(row, enabled) {
  row._switching = true;
  try {
    await setSchedulePageEnabledService(row.pageKey, enabled);
    row.enabled = enabled;
    ElMessage.success(enabled ? '公開しました' : '公開を停止しました');
  } catch (e) {
    console.error(e);
  } finally {
    row._switching = false;
  }
}

async function handleDelete(row) {
  try {
    await ElMessageBox.confirm(
      `「${row.movieTitle}」のページを削除しますか？公開リンクは使えなくなります。`,
      '確認',
      { confirmButtonText: '削除', cancelButtonText: 'キャンセル', type: 'warning' },
    );
  } catch {
    return;
  }
  try {
    await deleteSchedulePageService(row.pageKey);
    ElMessage.success('削除しました');
    loadList();
  } catch (e) {
    console.error(e);
  }
}

// ---------- 链接 ----------
async function copyLink(pageKey) {
  const url = buildPublicUrl(pageKey);
  try {
    await navigator.clipboard.writeText(url);
  } catch {
    // 非 https 等环境下 clipboard API 不可用时，用 textarea 兜底
    const el = document.createElement('textarea');
    el.value = url;
    el.setAttribute('readonly', '');
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
  }
  ElMessage.success('リンクをコピーしました');
}

function openPreview(pageKey) {
  window.open(buildPublicUrl(pageKey), '_blank', 'noopener');
}

// ---------- 作品选择（DP 的作品，限本公司；不传期间 = 全部作品，含未上映） ----------
// 每页条数与 LazyLoadMovieSelect 内部保持一致
const MOVIE_PAGE_SIZE = 50;
const movieOptions = ref([]);

// 作品候选请求：统一追加 sortOrder=desc，首屏 / 搜索 / 翻页都按公开日新→旧排列（不传时后端默认升序，供其他页面使用）
const fetchMovieOptions = (params) => getMovieListService({ ...params, sortOrder: 'desc' });

// 首屏候选：打开新建弹窗时取第一页，之后的搜索 / 翻页由 LazyLoadMovieSelect 自己请求
async function loadMovieFirstPage() {
  if (movieOptions.value.length) return;
  try {
    const res = await fetchMovieOptions({ companyId, page: 0, pageSize: MOVIE_PAGE_SIZE });
    movieOptions.value = Array.isArray(res) ? res : [];
  } catch (e) {
    console.error(e);
  }
}

// ---------- 影院主数据（EP） ----------
const cinemaMaster = ref([]);
const cinemaLoading = ref(false);
const cinemaLoadError = ref(false);
const pickerPref = ref('');
const pickerCinemaId = ref(null);

async function loadCinemaMaster() {
  if (cinemaMaster.value.length || cinemaLoading.value) return;
  cinemaLoading.value = true;
  cinemaLoadError.value = false;
  try {
    const rows = await fetchEpCinemaList();
    cinemaMaster.value = Array.isArray(rows) ? rows : [];
  } catch (e) {
    console.error(e);
    cinemaLoadError.value = true;
  } finally {
    cinemaLoading.value = false;
  }
}

const cinemaById = computed(() => new Map(cinemaMaster.value.map((c) => [Number(c.id), c])));
const prefOptions = computed(() => [...new Set(cinemaMaster.value.map((c) => c.prefecture).filter(Boolean))]);
const prefOf = (id) => cinemaById.value.get(Number(id))?.prefecture || '';

// 下拉选项：按都道府県过滤，已添加的置灰
const cinemaOptions = computed(() => {
  const added = new Set(form.cinemas.map((c) => Number(c.eglCinemaId)));
  return cinemaMaster.value
    .filter((c) => !pickerPref.value || c.prefecture === pickerPref.value)
    .map((c) => ({
      value: Number(c.id),
      label: `${c.name}（${c.prefecture || ''}${c.city ? ` ${c.city}` : ''}）`,
      disabled: added.has(Number(c.id)),
    }));
});

// ---------- 电话：保存值只对本页生效；没保存（null / 空）时观众页用默认值 ----------
// 默认值 = DP 影院主数据（cinemaContacts），没有时用 EP 的；只作为输入框的 placeholder，不作为 value
const dpTelephones = ref(new Map()); // eglCinemaId → DP 默认电话（'' = DP 里没有）

// 批量取 DP 默认电话（已取过的跳过；接口一次最多 1000 个）
async function loadDefaultTelephones(ids) {
  const pending = [...new Set(ids.map(Number))].filter((id) => !dpTelephones.value.has(id));
  if (!pending.length) return;
  const next = new Map(dpTelephones.value);
  for (let i = 0; i < pending.length; i += MAX_CINEMAS) {
    const chunk = pending.slice(i, i + MAX_CINEMAS);
    try {
      const res = await getCinemaContactsService(chunk);
      const found = new Map((Array.isArray(res?.data) ? res.data : [])
        .map((c) => [Number(c.eglCinemaId), c.telephone || '']));
      chunk.forEach((id) => next.set(id, found.get(id) || ''));
    } catch (e) {
      // 取不到时不缓存，下次还能重试；placeholder 先回退到 EP 的电话
      console.error(e);
    }
  }
  dpTelephones.value = next;
}

// 电话输入框的 placeholder：显示观众页实际会用的默认值
function telephonePlaceholder(row) {
  const id = Number(row.eglCinemaId);
  return dpTelephones.value.get(id) || cinemaById.value.get(id)?.telephone || '未設定';
}

/** 表单里的一行影院；telephone 只放保存值（null → ''），默认值走 placeholder */
function cinemaRow(data) {
  return {
    eglCinemaId: Number(data.eglCinemaId),
    cinemaName: data.cinemaName || '',
    note: data.note || '',
    // 与 form.extraColumns 按下标一一对应
    extraValues: form.extraColumns.map((_, i) => data.extraValues?.[i] ?? ''),
    telephone: data.telephone ?? '',
  };
}

function addCinema(id) {
  if (!id) return;
  pickerCinemaId.value = null;
  if (form.cinemas.length >= MAX_CINEMAS) {
    ElMessage.warning(`劇場は最大 ${MAX_CINEMAS} 件まで登録できます`);
    return;
  }
  if (form.cinemas.some((c) => Number(c.eglCinemaId) === Number(id))) return;
  const cinema = cinemaById.value.get(Number(id));
  form.cinemas.push(cinemaRow({ eglCinemaId: id, cinemaName: cinema?.name }));
  loadDefaultTelephones([id]);
}

// ---------- 追加項目（附加列） ----------
let extraColumnSeq = 0;
const extraColumn = (title = '') => ({ key: `extra-${extraColumnSeq += 1}`, title });

function addExtraColumn() {
  if (form.extraColumns.length >= MAX_EXTRA_COLUMNS) return;
  form.extraColumns.push(extraColumn());
  form.cinemas.forEach((c) => c.extraValues.push(''));
}

// 删除列时，每家影院对应下标的值也一起删掉，避免错位
function removeExtraColumn(index) {
  form.extraColumns.splice(index, 1);
  form.cinemas.forEach((c) => c.extraValues.splice(index, 1));
}

// 调整列顺序时，每家影院的值同步交换
function moveExtraColumn(index, delta) {
  const target = index + delta;
  if (target < 0 || target >= form.extraColumns.length) return;
  const swap = (list) => {
    [list[index], list[target]] = [list[target], list[index]];
  };
  swap(form.extraColumns);
  form.cinemas.forEach((c) => swap(c.extraValues));
}

function moveCinema(index, delta) {
  const target = index + delta;
  if (target < 0 || target >= form.cinemas.length) return;
  const [item] = form.cinemas.splice(index, 1);
  form.cinemas.splice(target, 0, item);
}

// ---------- 表单 ----------
const dialogVisible = ref(false);
const detailLoading = ref(false);
const saving = ref(false);
const formRef = ref();
const form = reactive(emptyForm());

function emptyForm() {
  return {
    pageKey: '',
    movieId: null,
    movieTitle: '',
    style: DEFAULT_STYLE,
    layout: DEFAULT_LAYOUT,
    pageNote: '',
    extraColumns: [],
    cinemas: [],
  };
}

const rules = {
  movieId: [{
    validator: (_rule, value, callback) => {
      // 编辑时作品不可改，不校验
      if (!form.pageKey && !value) callback(new Error('作品を選択してください'));
      else callback();
    },
    trigger: 'change',
  }],
};

function resetForm() {
  Object.assign(form, emptyForm());
  pickerPref.value = '';
  pickerCinemaId.value = null;
}

function openCreate() {
  resetForm();
  dialogVisible.value = true;
  loadCinemaMaster();
  loadMovieFirstPage();
}

async function openEdit(row) {
  resetForm();
  dialogVisible.value = true;
  loadCinemaMaster();
  detailLoading.value = true;
  try {
    const res = await getSchedulePageService(row.pageKey);
    const data = res?.data || {};
    Object.assign(form, {
      pageKey: data.pageKey,
      movieId: data.movieId,
      movieTitle: data.movieTitle,
      style: normalizeStyle(data.style),
      layout: normalizeLayout(data.layout),
      pageNote: data.pageNote || '',
      extraColumns: (data.extraColumns || []).map((title) => extraColumn(title || '')),
    });
    // cinemaRow 依赖 form.extraColumns，所以列先赋值、影院后赋值
    form.cinemas = (data.cinemas || []).map((c) => cinemaRow(c));
    // 默认电话一次批量取，作为 placeholder 显示
    loadDefaultTelephones(form.cinemas.map((c) => c.eglCinemaId));
  } catch (e) {
    console.error(e);
    dialogVisible.value = false;
  } finally {
    detailLoading.value = false;
  }
}

const savedPageKey = ref('');
const linkDialogVisible = ref(false);

async function submit() {
  try {
    await formRef.value.validate();
  } catch {
    return;
  }
  // 追加項目名：不能为空、不能重复
  const titles = form.extraColumns.map((col) => col.title.trim());
  if (titles.some((t) => !t)) {
    ElMessage.warning('追加項目の項目名を入力してください');
    return;
  }
  if (new Set(titles).size !== titles.length) {
    ElMessage.warning('追加項目の項目名が重複しています');
    return;
  }
  const payload = {
    movieId: form.movieId,
    style: form.style,
    layout: form.layout,
    pageNote: form.pageNote.trim(),
    extraColumns: titles,
    cinemas: form.cinemas.map((c) => ({
      eglCinemaId: c.eglCinemaId,
      // 影院名作为快照保存，排期没出来时观众页用它显示；优先用最新的主数据名称
      cinemaName: cinemaById.value.get(Number(c.eglCinemaId))?.name || c.cinemaName,
      note: (c.note || '').trim(),
      // 按列顺序传，没填的传 null
      extraValues: titles.map((_, i) => (c.extraValues[i] || '').trim() || null),
      // 输入了就保存输入值；没输入或清空则传 null（观众页用默认值）
      telephone: (c.telephone || '').trim() || null,
    })),
  };
  saving.value = true;
  try {
    let pageKey = form.pageKey;
    if (pageKey) {
      await updateSchedulePageService(pageKey, payload);
    } else {
      const res = await createSchedulePageService(payload);
      pageKey = res?.data;
    }
    dialogVisible.value = false;
    savedPageKey.value = pageKey;
    linkDialogVisible.value = !!pageKey;
    if (!pageKey) ElMessage.success('保存しました');
    loadList();
  } catch (e) {
    // 错误提示由 request.js 拦截器统一弹出（如作品不属于本公司、同一作品重复创建）
    console.error(e);
  } finally {
    saving.value = false;
  }
}

onMounted(loadList);
</script>

<style lang="scss">
@import './themes.scss';
</style>

<style lang="scss" scoped>
.sp-admin-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;

  h2 { margin: 0 0 4px; font-size: 18px; }
  p { margin: 0; font-size: 13px; color: var(--el-text-color-secondary); }
}
.sp-admin-actions { display: flex; gap: 8px; }
.sp-admin-movie { font-weight: 600; }
.sp-admin-empty { padding: 24px 0; color: var(--el-text-color-secondary); }
.sp-admin-help {
  margin: 0;
  font-size: 12px;
  line-height: 1.6;
  color: var(--el-text-color-secondary);
}
.sp-admin-error {
  margin: 0;
  font-size: 12px;
  color: var(--el-color-danger);
}
.sp-admin-movie-fixed {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 12px;
  font-weight: 600;
}

/* 列表里的配色小色块 */
.sp-admin-swatch-inline {
  display: inline-block;
  margin-right: 6px;
  vertical-align: middle;

  .sp-swatch-bg {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 16px;
    background: var(--sp-bg);
    border: 1px solid var(--sp-border-input);
    border-radius: 3px;
  }
  .sp-swatch-accent {
    width: 10px;
    height: 6px;
    background: var(--sp-accent);
    border-radius: 2px;
  }
}

/* 配色选择卡片：卡片本身套用对应配色 */
.sp-style-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
  width: 100%;
}
.sp-style-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 6px;
  background: transparent;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;

  &.is-selected { border-color: var(--el-color-primary); }
}
.sp-style-preview {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px;
  background: var(--sp-bg);
  border: 1px solid var(--sp-border);
  border-radius: 6px;
}
.sp-style-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--sp-text);
  border-left: 3px solid var(--sp-bar);
  padding-left: 6px;
}
.sp-style-times {
  display: flex;
  gap: 4px;

  span {
    padding: 2px 6px;
    font-size: 12px;
    font-weight: 700;
    color: var(--sp-accent);
    background: var(--sp-surface-card);
    border: 1px solid var(--sp-border);
    border-radius: 4px;
  }
}
.sp-style-desc {
  font-size: 12px;
  color: var(--el-text-color-regular);
}

.sp-cinema-picker {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}
.sp-cinema-picker-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.sp-cinema-picker-pref { width: 160px; }
.sp-cinema-picker-cinema { flex: 1; min-width: 240px; }
.sp-cinema-table { width: 100%; }

.sp-extra-columns {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}
.sp-extra-column-row {
  display: flex;
  align-items: center;
  gap: 4px;

  .el-input { flex: 1; max-width: 420px; }
}

.sp-link-row {
  display: flex;
  gap: 8px;
  width: 100%;
}
</style>
